import { createServerFn } from "@tanstack/react-start";
import { eq, desc, and, ilike, or, sql, getTableColumns } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug, calcReadTimeMinutes, calcReadTimeFromText } from "../utils/slug.server";
import { sanitizeHtml } from "../utils/sanitize.server";
import { ensureEditorDocPersisted } from "../doc/doc-migrate.server";
import { parseEditorDocInput } from "../doc/doc-types";
import { docPlainText } from "../doc/doc-convert";
import { invalidateInsightCache } from "./public.functions";
import { coverMediaInput, resolveCoverMediaInput } from "../media/input.server";
import { parseCraftStateInput } from "../craft/parse-craft-input.server";

const blockSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("p"), text: z.string() }),
  z.object({ type: z.literal("h2"), text: z.string() }),
  z.object({ type: z.literal("h3"), text: z.string() }),
  z.object({ type: z.literal("ul"), items: z.array(z.string()) }),
]);

const craftStateSchema = z.union([z.string(), z.record(z.unknown())]).optional().nullable();

function resolvePublishedAt(
  input: string | undefined,
  status: "draft" | "published" | "archived",
  existing?: Date | null,
): Date | null {
  if (input !== undefined && input.trim() !== "") {
    const parsed =
      input.length === 10 ? new Date(`${input}T12:00:00`) : new Date(input);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  if (status === "published") return existing ?? new Date();
  return existing ?? null;
}

const insightInput = coverMediaInput.extend({
  title: z.string().min(1),
  slug: z.string().optional(),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  publishedAt: z.string().optional(),
  excerpt: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  authorName: z.string().optional(),
  authorSlug: z.string().optional(),
  authorDesignation: z.string().optional(),
  contentFormat: z.enum(["blocks", "html", "both"]).default("blocks"),
  contentBlocks: z.array(blockSchema).optional(),
  contentHtml: z.string().optional(),
  craftState: craftStateSchema,
  craftCustomCss: z.string().optional().nullable(),
  editorDoc: z.array(z.record(z.unknown())).optional().nullable(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
});

export const listInsightsAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ search: z.string().optional(), status: z.string().optional() }).optional())
  .handler(async ({ data }) => {
    await requirePermission("insights", "read");
    const db = getDb();
    const conditions = [];
    if (data?.status) conditions.push(eq(schema.insights.status, data.status as "draft" | "published" | "archived"));
    if (data?.search) {
      conditions.push(
        or(
          ilike(schema.insights.title, `%${data.search}%`),
          ilike(schema.insights.slug, `%${data.search}%`),
        )!,
      );
    }
    return db
      .select({
        ...getTableColumns(schema.insights),
        impressions: sql<number>`coalesce(${schema.insightImpressions.impressions}, 0)`,
      })
      .from(schema.insights)
      .leftJoin(
        schema.insightImpressions,
        eq(schema.insights.id, schema.insightImpressions.insightId),
      )
      .where(conditions.length ? and(...conditions) : undefined)
      .orderBy(desc(schema.insights.updatedAt));
  });

export const getInsightAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "read");
    const db = getDb();
    const [row] = await db.select().from(schema.insights).where(eq(schema.insights.id, data.id)).limit(1);
    if (!row) return null;
    return ensureEditorDocPersisted(db, schema.insights, row);
  });

export const createInsight = createServerFn({ method: "POST" })
  .inputValidator(insightInput)
  .handler(async ({ data }) => {
    const user = await requirePermission("insights", "write");
    const db = getDb();
    const slug = data.slug || (await generateUniqueSlug(schema.insights, data.title));
    const blocks = data.contentBlocks ?? [];
    const media = await resolveCoverMediaInput(data);
    const editorDoc = data.editorDoc != null ? parseEditorDocInput(data.editorDoc) : null;
    const readTimeMinutes = editorDoc?.length
      ? calcReadTimeFromText(docPlainText(editorDoc))
      : calcReadTimeMinutes(blocks);
    const [row] = await db
      .insert(schema.insights)
      .values({
        title: data.title,
        slug,
        status: data.status,
        excerpt: data.excerpt,
        coverMediaId: media.coverMediaId,
        coverUrl: media.coverUrl,
        ogImageMediaId: media.ogImageMediaId,
        category: data.category,
        tags: data.tags ?? [],
        authorName: data.authorName,
        authorSlug: data.authorSlug,
        authorDesignation: data.authorDesignation,
        contentFormat: data.contentFormat,
        contentBlocks: blocks,
        contentHtml: data.contentHtml ? sanitizeHtml(data.contentHtml) : null,
        craftState: parseCraftStateInput(data.craftState),
        craftCustomCss: data.craftCustomCss ?? null,
        editorDoc,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        readTimeMinutes,
        publishedAt: resolvePublishedAt(data.publishedAt, data.status),
        createdBy: user.id,
        updatedBy: user.id,
      })
      .returning();
    await invalidateInsightCache(slug);
    return row;
  });

export const updateInsight = createServerFn({ method: "POST" })
  .inputValidator(insightInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("insights", "write");
    const db = getDb();
    const [existing] = await db.select().from(schema.insights).where(eq(schema.insights.id, data.id)).limit(1);
    if (!existing) throw new Error("Not found");

    const slug = data.slug || existing.slug;
    const blocks = data.contentBlocks ?? (existing.contentBlocks as typeof data.contentBlocks) ?? [];
    const media = await resolveCoverMediaInput(data);
    const editorDoc =
      data.editorDoc !== undefined
        ? parseEditorDocInput(data.editorDoc)
        : (existing.editorDoc as typeof data.editorDoc) ?? null;
    const readTimeMinutes = editorDoc?.length
      ? calcReadTimeFromText(docPlainText(editorDoc as import("../doc/doc-types").EditorDoc))
      : calcReadTimeMinutes(blocks);
    const [row] = await db
      .update(schema.insights)
      .set({
        title: data.title,
        slug,
        status: data.status,
        excerpt: data.excerpt,
        coverMediaId: media.coverMediaId,
        coverUrl: media.coverUrl,
        ogImageMediaId: media.ogImageMediaId,
        category: data.category,
        tags: data.tags ?? (Array.isArray(existing.tags) ? existing.tags : []),
        authorName: data.authorName,
        authorSlug: data.authorSlug,
        authorDesignation: data.authorDesignation,
        contentFormat: data.contentFormat,
        contentBlocks: blocks,
        contentHtml: data.contentHtml ? sanitizeHtml(data.contentHtml) : existing.contentHtml,
        craftState:
          data.craftState !== undefined
            ? parseCraftStateInput(data.craftState)
            : existing.craftState,
        craftCustomCss:
          data.craftCustomCss !== undefined ? data.craftCustomCss : existing.craftCustomCss,
        editorDoc,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        readTimeMinutes,
        publishedAt: resolvePublishedAt(data.publishedAt, data.status, existing.publishedAt),
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.insights.id, data.id))
      .returning();

    if (existing.slug !== slug) {
      await db.insert(schema.slugHistory).values({
        entityType: "insight",
        entityId: data.id,
        oldSlug: existing.slug,
        newSlug: slug,
      });
    }
    await invalidateInsightCache(existing.slug);
    await invalidateInsightCache(slug);
    return row;
  });

export const deleteInsight = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "delete");
    const db = getDb();
    const [existing] = await db.select().from(schema.insights).where(eq(schema.insights.id, data.id)).limit(1);
    await db.delete(schema.insights).where(eq(schema.insights.id, data.id));
    if (existing) await invalidateInsightCache(existing.slug);
    return { ok: true };
  });

export const generateInsightSlug = createServerFn({ method: "POST" })
  .inputValidator(z.object({ title: z.string(), excludeId: z.string().uuid().optional() }))
  .handler(async ({ data }) => {
    await requirePermission("insights", "write");
    return generateUniqueSlug(schema.insights, data.title, data.excludeId);
  });

export const duplicateInsight = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("insights", "write");
    const db = getDb();
    const [source] = await db.select().from(schema.insights).where(eq(schema.insights.id, data.id)).limit(1);
    if (!source) throw new Error("Not found");
    const slug = await generateUniqueSlug(schema.insights, `${source.title} copy`);
    const [row] = await db
      .insert(schema.insights)
      .values({
        title: `${source.title} (Copy)`,
        slug,
        status: "draft",
        excerpt: source.excerpt,
        coverMediaId: source.coverMediaId,
        coverUrl: source.coverUrl,
        ogImageMediaId: source.ogImageMediaId,
        category: source.category,
        tags: source.tags ?? [],
        authorName: source.authorName,
        authorSlug: source.authorSlug,
        authorDesignation: source.authorDesignation,
        contentFormat: source.contentFormat,
        contentBlocks: source.contentBlocks,
        contentHtml: source.contentHtml,
        craftState: source.craftState,
        craftCustomCss: source.craftCustomCss,
        editorDoc: source.editorDoc,
        metaTitle: source.metaTitle,
        metaDescription: source.metaDescription,
        readTimeMinutes: source.readTimeMinutes,
        createdBy: user.id,
        updatedBy: user.id,
      })
      .returning();
    return row;
  });
